/**
 * AI CONTEXT: mobile-first - User login page with AWS Cognito integration
 * 
 * This component implements user login with:
 * - Mobile-first responsive design
 * - AWS Cognito authentication
 * - Complete password reset functionality with multi-step flow
 * - Touch-friendly interface elements
 * - Proper error handling and user feedback
 * 
 * Architecture decisions:
 * - Use controlled components for form state
 * - Implement real-time validation
 * - Provide clear error messages
 * - Follow mobile-first design principles
 * - Multi-step password reset flow with proper state management
 * 
 * Future considerations:
 * - Add social login options
 * - Implement biometric authentication
 * - Add "remember me" functionality
 * - Consider passwordless authentication
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Lock, Eye, EyeOff, ArrowRight, Key } from 'lucide-react';
import { t } from '../i18n';
import { setOrCreateMeta } from '../utils/seo';

const Login = () => {
  const navigate = useNavigate();
  const { login, resetPassword, confirmResetPassword, loading } = useAuth();
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  
  // Password reset state management
  const [resetStep, setResetStep] = useState('email'); // 'email', 'code', 'password', 'success'
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  // AI CONTEXT: ux - Real-time form validation
  // Reasoning: Provide immediate feedback for better user experience
  // Constraints: Must validate all required fields
  // Future: Consider implementing more sophisticated validation rules

  const validateForm = () => {
    const newErrors = {};
    
    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // AI CONTEXT: ux - Handle form input changes
  // Reasoning: Update form state and clear errors as user types
  // Constraints: Must maintain form state consistency
  // Future: Consider implementing auto-save functionality

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // AI CONTEXT: security - Handle login submission
  // Reasoning: Authenticate user with AWS Cognito
  // Constraints: Must handle various login scenarios
  // Future: Consider implementing "remember me" functionality

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      const result = await login(formData.email, formData.password);
      console.log('Login result:', result); // Debug log
      
      if (result.success) {
        // Redirect admin users to admin dashboard, regular users to dashboard
        console.log('Login successful, checking admin status...'); // Debug log
        console.log('result.user:', result.user); // Debug log
        console.log('result.user.isAdmin:', result.user?.isAdmin); // Debug log
        console.log('Condition check:', result.user && result.user.isAdmin); // Debug log
        
        if (result.user && result.user.isAdmin) {
          console.log('Redirecting to admin dashboard'); // Debug log
          navigate('/admin');
        } else {
          console.log('Redirecting to dashboard'); // Debug log
          navigate('/dashboard');
        }
      } else {
        setErrors({ general: result.message || 'Login failed' });
      }
    } catch (error) {
      console.error('Login error:', error);
      setErrors({ general: 'An error occurred. Please try again.' });
    }
  };

  // AI CONTEXT: security - Handle password reset step 1: Send reset code
  // Reasoning: Allow users to reset forgotten passwords
  // Constraints: Must follow AWS Cognito password reset flow
  // Future: Consider implementing passwordless authentication

  const handleSendResetCode = async (e) => {
    e.preventDefault();
    
    if (!resetEmail) {
      setErrors({ resetEmail: 'Email is required' });
      return;
    }
    
    if (!/\S+@\S+\.\S+/.test(resetEmail)) {
      setErrors({ resetEmail: 'Please enter a valid email address' });
      return;
    }
    
    try {
      setResetLoading(true);
      const result = await resetPassword(resetEmail);
      
      if (result.success) {
        setResetStep('code');
        setErrors({});
      } else {
        setErrors({ resetEmail: result.message || 'Failed to send reset code' });
      }
    } catch (error) {
      console.error('Password reset error:', error);
      setErrors({ resetEmail: 'An error occurred. Please try again.' });
    } finally {
      setResetLoading(false);
    }
  };

  // AI CONTEXT: security - Handle password reset step 2: Verify code
  // Reasoning: Verify the reset code before allowing password change
  // Constraints: Must validate 6-digit code format
  // Future: Consider implementing code resend functionality

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    
    if (!resetCode) {
      setErrors({ resetCode: 'Reset code is required' });
      return;
    }
    
    if (resetCode.length !== 6) {
      setErrors({ resetCode: 'Please enter the complete 6-digit code' });
      return;
    }
    
    // Move to password entry step
    setResetStep('password');
    setErrors({});
  };

  // AI CONTEXT: security - Handle password reset step 3: Set new password
  // Reasoning: Complete password reset process securely
  // Constraints: Must verify reset code and new password
  // Future: Consider implementing password strength requirements

  const handleSetNewPassword = async (e) => {
    e.preventDefault();
    
    // Validate new password
    if (!newPassword) {
      setErrors({ newPassword: 'New password is required' });
      return;
    }
    
    if (newPassword.length < 8) {
      setErrors({ newPassword: 'Password must be at least 8 characters long' });
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setErrors({ confirmPassword: 'Passwords do not match' });
      return;
    }
    
    try {
      setResetLoading(true);
      const result = await confirmResetPassword(resetEmail, resetCode, newPassword);
      
      if (result.success) {
        setResetStep('success');
        setErrors({});
      } else {
        setErrors({ newPassword: result.message || 'Failed to reset password' });
      }
    } catch (error) {
      console.error('Password reset confirmation error:', error);
      setErrors({ newPassword: 'An error occurred. Please try again.' });
    } finally {
      setResetLoading(false);
    }
  };

  // AI CONTEXT: ux - Reset password reset flow
  // Reasoning: Allow users to start over if they make a mistake
  // Constraints: Must clear all reset state
  // Future: Consider implementing step-by-step navigation

  const resetPasswordFlow = () => {
    setResetStep('email');
    setResetEmail('');
    setResetCode('');
    setNewPassword('');
    setConfirmPassword('');
    setErrors({});
  };

  // AI CONTEXT: ux - Handle code input with auto-formatting
  // Reasoning: Improve user experience with proper code input handling
  // Constraints: Must handle 6-digit numeric codes
  // Future: Consider implementing auto-focus and paste handling

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setResetCode(value);
    setErrors(prev => ({ ...prev, resetCode: '' }));
  };

  const renderResetForm = () => {
    switch (resetStep) {
      case 'reset':
        return (
          <form onSubmit={handleSendResetCode} className="space-y-6">
            <div>
              <label htmlFor="resetEmail" className="block text-sm font-medium text-theme-silver mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-theme-silver-light h-5 w-5" />
                <input
                  type="email"
                  id="resetEmail"
                  value={resetEmail}
                  onChange={(e) => {
                    setResetEmail(e.target.value);
                    setErrors(prev => ({ ...prev, resetEmail: '' }));
                  }}
                  className={`w-full pl-10 pr-4 py-3 glass-input text-theme-silver placeholder-theme-silver-light focus:outline-none focus:ring-2 focus:ring-custom-orange/50 ${
                    errors.resetEmail ? 'border-red-500' : 'border-theme-silver/20'
                  }`}
                  placeholder="Enter your email address"
                />
              </div>
              {errors.resetEmail && (
                <p className="mt-1 text-sm text-red-400">{errors.resetEmail}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={resetLoading}
              className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {resetLoading ? 'Sending...' : 'Send Reset Code'}
            </button>
          </form>
        );

      case 'code':
        return (
          <form onSubmit={handleVerifyCode} className="space-y-6">
            <div>
              <label htmlFor="resetCode" className="block text-sm font-medium text-theme-silver mb-2">
                Reset Code
              </label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 text-theme-silver-light h-5 w-5" />
                <input
                  type="text"
                  id="resetCode"
                  value={resetCode}
                  onChange={handleCodeChange}
                  className={`w-full pl-10 pr-4 py-3 glass-input text-theme-silver placeholder-theme-silver-light focus:outline-none focus:ring-2 focus:ring-custom-orange/50 text-center text-lg tracking-widest ${
                    errors.resetCode ? 'border-red-500' : 'border-theme-silver/20'
                  }`}
                  placeholder="000000"
                  maxLength={6}
                />
              </div>
              {errors.resetCode && (
                <p className="mt-1 text-sm text-red-400">{errors.resetCode}</p>
              )}
              <p className="mt-2 text-sm text-theme-silver-light text-center">
                Enter the 6-digit code sent to {resetEmail}
              </p>
            </div>

            <button
              type="submit"
              disabled={resetLoading || resetCode.length !== 6}
              className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Verify Code
            </button>
          </form>
        );

      case 'password':
        return (
          <form onSubmit={handleSetNewPassword} className="space-y-6">
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-theme-silver mb-2">
                New Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-theme-silver-light h-5 w-5" />
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    setErrors(prev => ({ ...prev, newPassword: '' }));
                  }}
                  className={`w-full pl-10 pr-12 py-3 glass-input text-theme-silver placeholder-theme-silver-light focus:outline-none focus:ring-2 focus:ring-custom-orange/50 ${
                    errors.newPassword ? 'border-red-500' : 'border-theme-silver/20'
                  }`}
                  placeholder="Enter new password"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-theme-silver-light hover:text-theme-silver"
                >
                  {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {errors.newPassword && (
                <p className="mt-1 text-sm text-red-400">{errors.newPassword}</p>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-theme-silver mb-2">
                Confirm New Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-theme-silver-light h-5 w-5" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setErrors(prev => ({ ...prev, confirmPassword: '' }));
                  }}
                  className={`w-full pl-10 pr-12 py-3 glass-input text-theme-silver placeholder-theme-silver-light focus:outline-none focus:ring-2 focus:ring-custom-orange/50 ${
                    errors.confirmPassword ? 'border-red-500' : 'border-theme-silver/20'
                  }`}
                  placeholder="Confirm new password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-theme-silver-light hover:text-theme-silver"
                >
                  {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-400">{errors.confirmPassword}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={resetLoading || !newPassword || !confirmPassword}
              className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {resetLoading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        );

      case 'success':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-theme-silver mb-2">Password Reset Successful!</h3>
              <p className="text-theme-silver-light">
                Your password has been successfully reset. You can now sign in with your new password.
              </p>
            </div>

            <button
              onClick={() => {
                resetPasswordFlow();
                setResetStep('email');
              }}
              className="w-full btn-primary"
            >
              Back to Sign In
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  useEffect(() => {
    setOrCreateMeta('meta[name="robots"]', { name: 'robots' }, 'content', 'noindex,nofollow');
  }, []);

  return (
    <div className="min-h-screen bg-theme-black flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          {resetStep === 'email' ? (
            <>
              <h1 className="text-2xl font-bold text-theme-silver mb-2">{t('auth.welcomeBack')}</h1>
              <p className="text-theme-silver-light">{t('auth.signin')} to your {t('brand.name')} account</p>
            </>
          ) : resetStep === 'reset' ? (
            <>
              <h1 className="text-2xl font-bold text-theme-silver mb-2">Reset Password</h1>
              <p className="text-theme-silver-light">Enter your email to receive a reset code</p>
            </>
          ) : resetStep === 'code' ? (
            <>
              <h1 className="text-2xl font-bold text-theme-silver mb-2">Enter Reset Code</h1>
              <p className="text-theme-silver-light">We sent a code to your email</p>
            </>
          ) : resetStep === 'password' ? (
            <>
              <h1 className="text-2xl font-bold text-theme-silver mb-2">Set New Password</h1>
              <p className="text-theme-silver-light">Create a secure new password</p>
            </>
          ) : resetStep === 'success' ? (
            <>
              <h1 className="text-2xl font-bold text-theme-silver mb-2">Success!</h1>
              <p className="text-theme-silver-light">Your password has been reset</p>
            </>
          ) : null}
        </div>

        {resetStep === 'email' ? (
          /* Login Form */
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-theme-silver mb-2">
                 {t('auth.email')}
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-theme-silver-light h-5 w-5" />
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full pl-10 pr-4 py-3 glass-input text-theme-silver placeholder-theme-silver-light focus:outline-none focus:ring-2 focus:ring-custom-orange/50 ${
                    errors.email ? 'border-red-500' : 'border-theme-silver/20'
                  }`}
                   placeholder={t('auth.email')}
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-400">{errors.email}</p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-theme-silver mb-2">
                 {t('auth.password')}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-theme-silver-light h-5 w-5" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`w-full pl-10 pr-12 py-3 glass-input text-theme-silver placeholder-theme-silver-light focus:outline-none focus:ring-2 focus:ring-custom-orange/50 ${
                    errors.password ? 'border-red-500' : 'border-theme-silver/20'
                  }`}
                   placeholder={t('auth.password')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-theme-silver-light hover:text-theme-silver"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-400">{errors.password}</p>
              )}
            </div>

            {/* General Error */}
            {errors.general && (
              <div className="p-3 glass-card-dark border-red-500/30 rounded-lg">
                <p className="text-red-400 text-sm">{errors.general}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Signing In...' : t('auth.signin')}
            </button>
          </form>
        ) : (
          /* Password Reset Forms */
          renderResetForm()
        )}

        {/* Action Links */}
        <div className="mt-6 space-y-4">
          {resetStep === 'email' && (
            <>
              {/* Forgot Password */}
              <div className="text-center">
                <button
                  onClick={() => setResetStep('reset')}
                  className="text-custom-orange hover:text-custom-orange/80 text-sm font-medium"
                >
                  {t('auth.forgotPassword')}
                </button>
              </div>

              {/* Register Link */}
              <div className="text-center">
                <p className="text-theme-silver-light">
                  Don't have an account?{' '}
                  <Link to="/register" className="text-custom-orange hover:text-custom-orange/80 font-medium">
                    {t('auth.createAccountCta')}
                  </Link>
                </p>
              </div>
            </>
          )}
          
          {resetStep !== 'email' && resetStep !== 'success' && (
            /* Back to Login */
            <div className="text-center">
              <button
                onClick={resetPasswordFlow}
                className="inline-flex items-center text-custom-orange hover:text-custom-orange/80 font-medium"
              >
                <ArrowRight className="h-4 w-4 mr-2 rotate-180" />
                 {t('auth.backToSignIn')}
              </button>
            </div>
          )}
        </div>

        {/* Legal Notice */}
        <div className="mt-8 glass-card-dark p-4 rounded-lg">
          <p className="text-xs text-theme-silver-light text-center">
            By signing in, you agree to our{' '}
            <Link to="/legal" className="text-custom-orange hover:text-custom-orange/80">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link to="/legal" className="text-custom-orange hover:text-custom-orange/80">
              Privacy Policy
            </Link>
            . You must be 21 or older to participate in firearm giveaways.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login; 